import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import { useAuthContext } from '@/contexts/AuthContext';
import WhatsAppChat from '@/components/feature/WhatsAppChat';

interface PipelineStage {
  id: string;
  name: string;
  color: string;
  order_index: number;
}

interface Lead {
  id: string;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  document: string | null;
  document_type: 'cpf' | 'cnpj' | null;
  source: string | null;
  segment: string | null;
  main_interest: string | null;
  notes: string | null;
  stage_id: string | null;
  assigned_to: string | null;
  next_contact_date: string | null;
  status: 'ativo' | 'convertido' | 'perdido' | 'arquivado';
  converted_at: string | null;
  created_at: string;
  assigned_profile: { full_name: string; email: string } | null;
}

interface Profile {
  id: string;
  full_name: string;
  email: string;
}

export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('list');
  const [activeModalTab, setActiveModalTab] = useState<'info' | 'whatsapp'>('info');

  // Filters
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('ativo');
  const [filterAssigned, setFilterAssigned] = useState<string>('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [formData, setFormData] = useState<Partial<Lead>>({
    status: 'ativo',
  });

  // Convert modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingLead, setConvertingLead] = useState<Lead | null>(null);
  const [createProject, setCreateProject] = useState(false);
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location.state?.openLeadId && leads.length > 0) {
      const lead = leads.find(l => l.id === location.state.openLeadId);
      if (lead) {
        openEditModal(lead);
        if (location.state.openTab === 'whatsapp') {
          setActiveModalTab('whatsapp');
        }
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state, leads]);

  const loadData = async () => {
    setLoading(true);

    const [stagesRes, leadsRes, profilesRes] = await Promise.all([
      supabase.from('pipeline_stages').select('*').eq('is_active', true).order('order_index'),
      supabase.from('leads').select('*, assigned_profile:profiles!assigned_to(full_name, email)').eq('is_active', true).order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, full_name, email').eq('is_active', true),
    ]);

    if (stagesRes.error) {
      console.error('Error loading stages:', stagesRes.error);
      alert('Erro ao carregar etapas: ' + stagesRes.error.message);
    }
    if (leadsRes.error) {
      console.error('Error loading leads:', leadsRes.error);
      alert('Erro ao carregar leads: ' + leadsRes.error.message);
    }
    if (profilesRes.error) {
      console.error('Error loading perfis:', profilesRes.error);
      alert('Erro ao carregar perfis: ' + profilesRes.error.message);
    }

    setStages(stagesRes.data || []);
    setLeads(leadsRes.data || []);
    setProfiles(profilesRes.data || []);
    setLoading(false);
  };

  const filteredLeads = leads.filter((l) => {
    const matchesSearch =
      !search ||
      l.company_name.toLowerCase().includes(search.toLowerCase()) ||
      (l.contact_name && l.contact_name.toLowerCase().includes(search.toLowerCase())) ||
      (l.email && l.email.toLowerCase().includes(search.toLowerCase())) ||
      (l.phone && l.phone.includes(search));

    const matchesStage = !filterStage || l.stage_id === filterStage;
    const matchesStatus = !filterStatus || l.status === filterStatus;
    const matchesAssigned = !filterAssigned || l.assigned_to === filterAssigned;

    return matchesSearch && matchesStage && matchesStatus && matchesAssigned;
  });

  const handleSaveLead = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      ...formData,
      is_active: true,
      updated_at: new Date().toISOString(),
    };

    if (editingLead) {
      const { error } = await supabase.from('leads').update(payload).eq('id', editingLead.id);
      if (!error && user) {
        await logAudit(user.id, 'lead_updated', 'leads', editingLead.id, undefined, { company_name: formData.company_name });
      }
    } else {
      const { data, error } = await supabase.from('leads').insert([payload]).select().single();
      if (!error && data && user) {
        await logAudit(user.id, 'lead_created', 'leads', data.id, undefined, { company_name: formData.company_name });
      }
    }

    setShowModal(false);
    setEditingLead(null);
    setFormData({ status: 'ativo' });
    loadData();
  };

  const handleDeleteLead = async (lead: Lead) => {
    if (!confirm(`Deseja realmente excluir o lead "${lead.company_name}"?`)) return;

    await supabase.from('leads').update({ is_active: false }).eq('id', lead.id);
    if (user) await logAudit(user.id, 'lead_deleted', 'leads', lead.id, { company_name: lead.company_name });
    loadData();
  };

  const handleConvertLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!convertingLead) return;

    // Create client
    const { data: clientData, error: clientError } = await supabase
      .from('clients')
      .insert([
        {
          name: convertingLead.company_name,
          document: convertingLead.document,
          document_type: convertingLead.document_type,
          email: convertingLead.email,
          phone: convertingLead.phone || convertingLead.whatsapp,
          notes: convertingLead.notes,
        },
      ])
      .select()
      .single();

    if (clientError || !clientData) {
      alert('Erro ao criar cliente: ' + clientError?.message);
      return;
    }

    // Create project if requested
    if (createProject && projectName.trim()) {
      await supabase.from('projects').insert([
        {
          name: projectName.trim(),
          client_id: clientData.id,
          status: 'proposta',
        },
      ]);
    }

    // Update lead as converted
    await supabase
      .from('leads')
      .update({
        status: 'convertido',
        converted_at: new Date().toISOString(),
        converted_to_client_id: clientData.id,
      })
      .eq('id', convertingLead.id);

    if (user) await logAudit(user.id, 'lead_converted', 'leads', convertingLead.id, undefined, {
      company_name: convertingLead.company_name,
      client_id: clientData.id,
    });

    setShowConvertModal(false);
    setConvertingLead(null);
    setCreateProject(false);
    setProjectName('');
    loadData();
  };

  const openEditModal = (lead: Lead) => {
    setEditingLead(lead);
    setFormData(lead);
    setActiveModalTab('info');
    setShowModal(true);
  };

  const openWhatsApp = (lead: Lead) => {
    setEditingLead(lead);
    setFormData(lead);
    setActiveModalTab('whatsapp');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingLead(null);
    setFormData({
      status: 'ativo',
      stage_id: stages.length > 0 ? stages[0].id : undefined,
      assigned_to: user?.id,
    });
    setActiveModalTab('info');
    setShowModal(true);
  };

  const openConvertModal = (lead: Lead) => {
    setConvertingLead(lead);
    setProjectName(`Projeto ${lead.company_name}`);
    setShowConvertModal(true);
  };

  const formatPhone = (value: string | null) => {
    if (!value) return '—';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return value;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getStageById = (id: string | null) => stages.find((s) => s.id === id);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-sky-100 text-sky-700';
      case 'convertido':
        return 'bg-emerald-100 text-emerald-700';
      case 'perdido':
        return 'bg-red-100 text-red-700';
      case 'arquivado':
        return 'bg-slate-100 text-slate-600';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const leadsByStage = stages.map((stage) => ({
    ...stage,
    leads: filteredLeads.filter((l) => l.stage_id === stage.id && l.status === 'ativo'),
  }));

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-slate-800 text-2xl font-bold">Leads & Pipeline</h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie leads e acompanhe o funil de vendas</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                viewMode === 'list' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <i className="ri-list-check mr-1.5"></i>Lista
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                viewMode === 'kanban' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <i className="ri-kanban-view mr-1.5"></i>Kanban
            </button>
          </div>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5"></i>Novo Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 rounded-xl border border-slate-200 bg-white">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por nome, email, telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
            />
          </div>
        </div>

        <select
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white"
        >
          <option value="">Todas as etapas</option>
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white"
        >
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="convertido">Convertido</option>
          <option value="perdido">Perdido</option>
          <option value="arquivado">Arquivado</option>
        </select>

        <select
          value={filterAssigned}
          onChange={(e) => setFilterAssigned(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white"
        >
          <option value="">Todos os responsáveis</option>
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {p.full_name || p.email}
            </option>
          ))}
        </select>

        {(search || filterStage || filterStatus || filterAssigned) && (
          <button
            onClick={() => {
              setSearch('');
              setFilterStage('');
              setFilterStatus('ativo');
              setFilterAssigned('');
            }}
            className="px-3 py-2 text-slate-500 text-sm hover:text-slate-700 cursor-pointer"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View */
        <div className="flex gap-4 overflow-x-auto pb-4">
          {leadsByStage.map((stage) => (
            <div key={stage.id} className="w-72 shrink-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }}></div>
                <h3 className="font-semibold text-slate-700 text-sm">{stage.name}</h3>
                <span className="ml-auto text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{stage.leads.length}</span>
              </div>
              <div className="space-y-3">
                {stage.leads.map((lead) => (
                  <div
                    key={lead.id}
                    className="p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => openEditModal(lead)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-medium text-slate-800 text-sm line-clamp-2">{lead.company_name}</h4>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openConvertModal(lead);
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 cursor-pointer"
                          title="Converter em cliente"
                        >
                          <i className="ri-check-double-line text-sm"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLead(lead);
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 cursor-pointer"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openWhatsApp(lead);
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 cursor-pointer"
                          title="Falar no WhatsApp"
                        >
                          <i className="ri-whatsapp-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                    {lead.contact_name && <p className="text-slate-500 text-xs mb-1">{lead.contact_name}</p>}
                    {lead.email && <p className="text-slate-400 text-xs mb-1 truncate">{lead.email}</p>}
                    {lead.phone && <p className="text-slate-400 text-xs">{formatPhone(lead.phone)}</p>}
                    {lead.next_contact_date && (
                      <div className="flex items-center gap-1 mt-2 text-amber-600 text-xs">
                        <i className="ri-calendar-line"></i>
                        <span>{formatDate(lead.next_contact_date)}</span>
                      </div>
                    )}
                  </div>
                ))}
                {stage.leads.length === 0 && (
                  <div className="p-4 rounded-xl border border-dashed border-slate-200 text-center">
                    <p className="text-slate-400 text-xs">Nenhum lead nesta etapa</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Empresa/Contato</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Contato</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Etapa</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Responsável</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Próximo contato</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{lead.company_name}</p>
                    {lead.contact_name && <p className="text-slate-500 text-xs">{lead.contact_name}</p>}
                  </td>
                  <td className="px-4 py-3">
                    {lead.email && <p className="text-slate-600 text-xs">{lead.email}</p>}
                    {lead.phone && <p className="text-slate-400 text-xs">{formatPhone(lead.phone)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    {lead.stage_id ? (
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: getStageById(lead.stage_id)?.color }}
                        ></div>
                        <span className="text-slate-600 text-xs">{getStageById(lead.stage_id)?.name}</span>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-slate-600 text-xs">
                      {lead.assigned_profile?.full_name || lead.assigned_profile?.email || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-slate-600 text-xs">{formatDate(lead.next_contact_date)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                      {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {lead.status === 'ativo' && (
                        <button
                          onClick={() => openConvertModal(lead)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 cursor-pointer"
                          title="Converter em cliente"
                        >
                          <i className="ri-check-double-line"></i>
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal(lead)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteLead(lead)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 cursor-pointer"
                        title="Excluir"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                      <button
                        onClick={() => openWhatsApp(lead)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 cursor-pointer"
                        title="Falar no WhatsApp"
                      >
                        <i className="ri-whatsapp-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredLeads.length === 0 && (
            <div className="p-8 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 mx-auto mb-3">
                <i className="ri-user-search-line text-slate-400 text-xl"></i>
              </div>
              <p className="text-slate-500 text-sm">Nenhum lead encontrado</p>
            </div>
          )}
        </div>
      )}

      {/* Lead Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 z-10">
              <div className="px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">
                  {editingLead ? 'Detalhes do Lead' : 'Novo Lead'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              
              {editingLead && (
                <div className="flex px-6 gap-6">
                  <button
                    onClick={() => setActiveModalTab('info')}
                    className={`pb-3 text-sm font-medium transition-colors relative cursor-pointer ${
                      activeModalTab === 'info' ? 'text-cyan-600' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Informações
                    {activeModalTab === 'info' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-600 rounded-full"></div>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveModalTab('whatsapp')}
                    className={`pb-3 text-sm font-medium transition-colors relative cursor-pointer flex items-center gap-1.5 ${
                      activeModalTab === 'whatsapp' ? 'text-emerald-600' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <i className="ri-whatsapp-line"></i>
                    WhatsApp
                    {activeModalTab === 'whatsapp' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 rounded-full"></div>
                    )}
                  </button>
                </div>
              )}
            </div>

            {activeModalTab === 'info' ? (
              <form onSubmit={handleSaveLead} className="p-6 space-y-6">
              {/* Company Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Nome da Empresa/Pessoa *</label>
                  <input
                    type="text"
                    required
                    value={formData.company_name || ''}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Nome do Contato</label>
                  <input
                    type="text"
                    value={formData.contact_name || ''}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">E-mail</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Telefone</label>
                  <input
                    type="text"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">WhatsApp</label>
                  <input
                    type="text"
                    value={formData.whatsapp || ''}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Document */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Documento (CPF/CNPJ)</label>
                  <input
                    type="text"
                    value={formData.document || ''}
                    onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Tipo</label>
                  <select
                    value={formData.document_type || ''}
                    onChange={(e) => setFormData({ ...formData, document_type: e.target.value as 'cpf' | 'cnpj' })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white"
                  >
                    <option value="">Selecione</option>
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                  </select>
                </div>
              </div>

              {/* Pipeline & Assignment */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Etapa do Pipeline</label>
                  <select
                    value={formData.stage_id || ''}
                    onChange={(e) => setFormData({ ...formData, stage_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white"
                  >
                    <option value="">Selecione</option>
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Responsável</label>
                  <select
                    value={formData.assigned_to || ''}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white"
                  >
                    <option value="">Selecione</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name || p.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Próximo Contato</label>
                  <input
                    type="date"
                    value={formData.next_contact_date || ''}
                    onChange={(e) => setFormData({ ...formData, next_contact_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Source & Segment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Origem do Lead</label>
                  <input
                    type="text"
                    value={formData.source || ''}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="Ex: Indicação, Site, Instagram"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Segmento</label>
                  <input
                    type="text"
                    value={formData.segment || ''}
                    onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                    placeholder="Ex: E-commerce, SaaS, Serviços"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Interest */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Interesse Principal</label>
                <input
                  type="text"
                  value={formData.main_interest || ''}
                  onChange={(e) => setFormData({ ...formData, main_interest: e.target.value })}
                  placeholder="Ex: Site institucional, Sistema de gestão, App mobile"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Observações</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 cursor-pointer"
                >
                  {editingLead ? 'Salvar alterações' : 'Criar lead'}
                </button>
              </div>
            </form>
            ) : (
              <div className="p-6">
                {editingLead && (
                  <WhatsAppChat 
                    leadId={editingLead.id} 
                    contactName={editingLead.contact_name || editingLead.company_name}
                    phoneNumber={editingLead.whatsapp || editingLead.phone || ''}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {showConvertModal && convertingLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Converter Lead em Cliente</h2>
            </div>

            <form onSubmit={handleConvertLead} className="p-6 space-y-4">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-emerald-800 text-sm">
                  <i className="ri-information-line mr-1"></i>
                  O lead <strong>{convertingLead.company_name}</strong> será convertido em um cliente.
                  Todos os dados cadastrados serão aproveitados.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="createProject"
                  checked={createProject}
                  onChange={(e) => setCreateProject(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                />
                <label htmlFor="createProject" className="text-sm text-slate-700 cursor-pointer">
                  Criar primeiro projeto para este cliente
                </label>
              </div>

              {createProject && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Nome do Projeto</label>
                  <input
                    type="text"
                    required={createProject}
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowConvertModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 cursor-pointer"
                >
                  <i className="ri-check-line mr-1"></i>
                  Converter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
